import { useRef, useState } from "react";

import { Badge, Grid, Paper } from "@mui/material";
import { Stack } from "@mui/system";
import { useTranslation } from "react-i18next";

import { ReactComponent as Verify } from "../../../Assets/Images/VerifiedICON.svg";
import UserAvatar from "../../../Components/atoms/UserAvatar/UserAvatar";
import {
  SubHeader,
  SubHeader2,
  SubHeader5,
} from "../../atoms/Typography/SubHeader/SubHeader";
import { ESignature } from "../../molecules/ESignature/ESignature";
import { ConfirmMessageBox } from "../../templates/MessageBox/ConfirmMessageBox/ConfirmMessageBox";
import { LanguageMessage } from "../LanguageMessage/LanguageMessage";

export const DoctorInfoSection = ({
  watchAll,
  register,
  signature,
  setSignature,
  data,
}) => {
  const { t } = useTranslation();
  const [isOpenESignature, setIsOpenESignature] = useState(false);
  const [isOpenLangauge, setIsOpenLangauge] = useState(false);
  const eSign = useRef();
  const rawUserName = [data?.UserName, data?.userName].find((value) =>
    typeof value === "string" ? value.trim() !== "" : value,
  );
  const userName =
    typeof rawUserName === "string" ? rawUserName.trim() : rawUserName || "-";

  return (
    <>
      <Paper variant="White0.5" sx={{ width: "1264px" }}>
        <Grid container direction={"column"} spacing={4}>
          <Grid item>
            <Grid
              container
              direction={"row"}
              spacing={6}
              alignItems={"center"}
              justifyItems={"center"}
            >
              <Grid item>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  sx={{ zIndex: 0 }}
                >
                  <UserAvatar
                    dimention={"200px"}
                    src={data.profileImage || ""}
                  />
                </Badge>
              </Grid>

              <Grid item>
                <Stack spacing={3.7}>
                  <Stack>
                    <SubHeader text={t("common:table-field.name")} />
                    <Stack maxWidth={"200px"}>
                      <SubHeader2 text={data.fullName || "-"} />
                    </Stack>
                  </Stack>

                  <Stack>
                    <SubHeader text={t("glossary:doctor-details.user-name")} />
                    <Stack maxWidth={"200px"}>
                      <SubHeader2 text={userName} />
                    </Stack>
                  </Stack>

                  <Stack>
                    <SubHeader text={t("glossary:doctor-details.tagline")} />
                    <Stack maxWidth={"200px"}>
                      <SubHeader2 text={data.tagLine || "-"} />
                    </Stack>
                  </Stack>

                  <Stack>
                    <SubHeader text={t("glossary:doctor-details.university")} />
                    <Stack maxWidth={"200px"}>
                      <SubHeader2
                        text={
                          Array.isArray(data?.university)
                            ? data?.university
                                ?.map((i) => i.university)
                                .join(", ")
                            : "-"
                        }
                      />
                    </Stack>
                  </Stack>

                  <Stack>
                    <SubHeader
                      text={t("glossary:doctor-details.uni-specialty")}
                    />
                    <Stack maxWidth={"200px"}>
                      <SubHeader2
                        text={
                          Array.isArray(data.universitySpeciality) &&
                          data.universitySpeciality
                            ? data.universitySpeciality
                                .map((i) => i.qualification)
                                .join(", ")
                            : "-"
                        }
                      />
                    </Stack>
                  </Stack>                  
                </Stack>
              </Grid>
              <Grid item>
                <Stack spacing={3.7} marginLeft={"200px"}>
                  <Stack>
                    <SubHeader text={t("glossary:doctor-details.address")} />
                    <Stack
                      maxWidth={"500px"}
                      display={"flex"}
                      flexDirection={"row"}
                    >
                      <SubHeader2
                        text={
                          data.address.length < 1 || data.address.trim() === ","
                            ? "-"
                            : data.address.trim()
                        }
                      />
                    </Stack>
                  </Stack>

                  <Stack>
                    <SubHeader
                      text={t("glossary:doctor-details.prof-license-no")}
                    />

                    <Stack display={"flex"} flexDirection={"row"}>
                      <Stack maxWidth={"200px"}>
                        <SubHeader2
                          text={data.professionalLiceneseNumber || "-"}
                        />
                      </Stack>

                      {data.professionalLiceneseNumber && data.verifyDoctor && (
                        <>
                          <div style={{ marginLeft: "7px" }} />
                          <Verify />
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Stack>
                    <SubHeader
                      text={t("glossary:doctor-details.registration-no")}
                    />
                    <Stack display={"flex"} flexDirection={"row"}>
                      <Stack maxWidth={"200px"}>
                        <SubHeader2 text={data.registrationNo || "-"} />
                      </Stack>
                      {data.registrationNo && data.verifyDoctor && (
                        <>
                          <div style={{ marginLeft: "7px" }} />
                          <Verify />
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Stack>
                    <SubHeader
                      text={t("glossary:doctor-details.specialty-no")}
                    />
                    <Stack display={"flex"} flexDirection={"row"}>
                      <Stack maxWidth={"200px"}>
                        <SubHeader2
                          text={
                            Array.isArray(data?.specialityNumber)
                              ? data?.specialityNumber
                                  ?.map((i) => i.specialityNumber)
                                  .join(", ")
                              : "-"
                          }
                        />
                      </Stack>
                      {data.specialityNumber && data.verifyDoctor && (
                        <>
                          <div style={{ marginLeft: "7px" }} />
                          <Verify />
                        </>
                      )}
                    </Stack>
                  </Stack>

                  <Stack>
                    <SubHeader
                      text={t(
                        "glossary:doctor-details.specialty-based-on-reg-id",
                      )}
                    />
                    <Stack
                      maxWidth={"550px"}
                      display={"flex"}
                      flexDirection={"row"}
                    >
                      <Stack maxWidth={"200px"}>
                        <SubHeader2
                          text={
                            data.speciality && data.speciality.length > 0
                              ? data.speciality
                                  .map((specialty) => specialty.description)
                                  .join(", ")
                              : "-"
                          }
                        />
                      </Stack>
                      {data.speciality && data.verifyDoctor && (
                        <>
                          <div
                            style={{
                              position: "relative",
                              left:
                                data.speciality && data.speciality.length > 3
                                  ? "-14px"
                                  : "7px",
                            }}
                          >
                            <Verify />
                          </div>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Grid>

          <Grid item minWidth={"1194px"}>
            <Stack>
              <SubHeader text={t("glossary:doctor-details.biography")} />
              <Stack maxWidth={"200px"}>
                <SubHeader2 text={data.biography || "-"} />
              </Stack>
            </Stack>
          </Grid>
          <Grid item>
            <Grid container direction={"row"} justifyContent={"space-between"}>
              <Grid item>
                <Stack>
                  <SubHeader text={t("glossary:doctor-details.experience")} />
                  <Stack maxWidth={"200px"}>
                    <SubHeader2 text={data.experience || "-"} />
                  </Stack>
                </Stack>
              </Grid>

              {/* Languages section */}
              <Grid item>
                <SubHeader text={t("glossary:doctor-details.langauges")} />
                <Stack direction={"row"}>
                  {data.languages && data.languages.length > 0 ? (
                    data.languages.map((language, i) => {
                      return (
                        <Paper
                          variant="LightGreen"
                          style={{
                            margin: "3.5px",
                            marginLeft: i == 0 ? 0 : "3.5px",
                          }}
                        >
                          <Grid
                            container
                            direction={"row"}
                            spacing={2}
                            paddingLeft={"20px"}
                            paddingRight={"20px"}
                          >
                            <Grid item>
                              <SubHeader5 text={language.languageName} />
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })
                  ) : (
                    <SubHeader2 text={"-"} />
                  )}
                </Stack>
              </Grid>

              {/* Treatment interests section */}
              <Grid item>
                <SubHeader
                  text={t("glossary:doctor-details.treatment-interests")}
                />
                <Stack direction={"row"}>
                  {data.treatmentInterests &&
                  data.treatmentInterests.length > 0 ? (
                    data.treatmentInterests.map((interest, i) => {
                      return (
                        <Paper
                          variant="LightGreen"
                          style={{
                            margin: "3.5px",
                            marginLeft: i == 0 ? 0 : "3.5px",
                          }}
                        >
                          <Grid
                            container
                            direction={"row"}
                            spacing={2}
                            paddingLeft={"20px"}
                            paddingRight={"20px"}
                          >
                            <Grid item>
                              <SubHeader5 text={interest.treatmentInterest} />
                            </Grid>
                          </Grid>
                        </Paper>
                      );
                    })
                  ) : (
                    <SubHeader2 text={"-"} />
                  )}
                </Stack>
              </Grid>
              <Grid item>
                <SubHeader text={t("glossary:doctor-details.your-e-sign")} />
                <Stack
                  direction={"row"}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                >
                  {data.signature ? (
                    <img
                      src={"data:image/jpeg;base64," + data.signature}
                      width={"65px"}
                      height={"44px"}
                    />
                  ) : (
                    <SubHeader2 text={"-"} />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <ConfirmMessageBox
        isOpen={isOpenESignature}
        setIsOpen={setIsOpenESignature}
        positiveButton={"Accept"}
        negativeButton={"Clear"}
        negativeButtonAction={() => {
          eSign.current.clearHandler();
        }}
        positiveButtonAction={() => {
          eSign.current.saveSign();
          setIsOpenESignature(!isOpenESignature);
        }}
      >
        <ESignature ref={eSign} setSignature={setSignature} />
      </ConfirmMessageBox>
      <ConfirmMessageBox
        isOpen={isOpenLangauge}
        setIsOpen={setIsOpenLangauge}
        positiveButton={"Accept"}
        negativeButton={"Clear"}
        negativeButtonAction={() => {
          setIsOpenLangauge(!isOpenLangauge);
        }}
        positiveButtonAction={() => {
          setIsOpenLangauge(!isOpenLangauge);
        }}
      >
        <LanguageMessage register={register} watch={watchAll} />
      </ConfirmMessageBox>
    </>
  );
};
